import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePermission } from '@/hooks/useAuth'
import { salesLeadKeys } from '@/modules/sales/hooks/useSalesLeads'
import {
  siteInboxService,
  type SiteInboxAddInput,
  type SiteInboxEditInput,
  type SiteInboxListParams,
  type SiteInboxRejectReason,
} from '@/modules/sales/services/site-inbox.service'

export const siteInboxKeys = {
  all: ['site-inbox'] as const,
  list: (params: SiteInboxListParams) => [...siteInboxKeys.all, 'list', params] as const,
  counts: ['site-inbox', 'counts'] as const,
  detail: (id: string) => [...siteInboxKeys.all, 'detail', id] as const,
}

export function useSiteInboxList(params: SiteInboxListParams, enabled = true) {
  return useQuery({
    queryKey: siteInboxKeys.list(params),
    queryFn: () => siteInboxService.list(params),
    placeholderData: keepPreviousData,
    enabled,
  })
}

/** Waiting count for the Inbox tab (light list call, refreshed every minute). */
export function useSiteInboxCount(enabled: boolean) {
  return useQuery({
    queryKey: siteInboxKeys.counts,
    queryFn: async () => (await siteInboxService.list({ status: 'pending', pageSize: 1 })).counts,
    enabled,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
}

/** Inbox tab for the Leads tab bars — null when the user can't review. */
export function useInboxTabItem() {
  const canInbox = usePermission('sales_leads.inbox')
  const { data } = useSiteInboxCount(canInbox)
  if (!canInbox) return null
  const waiting = data?.pending ?? null
  return { id: 'inbox', label: 'Inbox', count: waiting, attention: Boolean(waiting) }
}

export function useSiteInboxEnquiry(id: string | undefined) {
  return useQuery({
    queryKey: siteInboxKeys.detail(id ?? ''),
    queryFn: () => siteInboxService.get(id!),
    enabled: Boolean(id),
  })
}

export function useSiteInboxMutations(id: string) {
  const qc = useQueryClient()
  const done = (addedQuery = false) => {
    void qc.invalidateQueries({ queryKey: siteInboxKeys.all })
    if (addedQuery) void qc.invalidateQueries({ queryKey: salesLeadKeys.all })
  }
  return {
    save: useMutation({
      mutationFn: (input: SiteInboxEditInput) => siteInboxService.save(id, input),
      onSuccess: () => done(),
    }),
    add: useMutation({
      mutationFn: (input: SiteInboxAddInput) => siteInboxService.add(id, input),
      onSuccess: () => done(true),
      // Someone else may have added / rejected it meanwhile.
      onError: () => done(),
    }),
    reject: useMutation({
      mutationFn: (input: { reason: SiteInboxRejectReason; note?: string | null }) => siteInboxService.reject(id, input),
      onSuccess: () => done(),
      onError: () => done(),
    }),
    restore: useMutation({
      mutationFn: () => siteInboxService.restore(id),
      onSuccess: () => done(),
    }),
    remove: useMutation({
      mutationFn: () => siteInboxService.delete(id),
      onSuccess: () => done(),
    }),
  }
}

/** List-level reject / restore / delete (row id passed per call). */
export function useSiteInboxListMutations() {
  const qc = useQueryClient()
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: siteInboxKeys.all })
  }
  return {
    reject: useMutation({
      mutationFn: ({ id, reason, note }: { id: string; reason: SiteInboxRejectReason; note?: string | null }) =>
        siteInboxService.reject(id, { reason, note }),
      onSuccess: invalidate,
      onError: invalidate,
    }),
    restore: useMutation({
      mutationFn: (id: string) => siteInboxService.restore(id),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) => siteInboxService.delete(id),
      onSuccess: invalidate,
    }),
  }
}
